import { Component, inject, OnInit } from '@angular/core';
import Keycloak from 'keycloak-js';
import { NavigationService, NavigationItem, Role } from 'shared-lib';
import { VerticalNavComponent } from 'shared-lib';
import { TranslateService } from '@ngx-translate/core';
import { RoleService } from '../auth/role-service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [VerticalNavComponent],
  template: '<lib-vertical-nav [environment]="environment" [navigationItems]="navigationItems" [showLanguageSwitcher]="true"></lib-vertical-nav>',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit {
  private readonly roleService = inject(RoleService);
  private readonly keycloak = inject(Keycloak);
  private navigationService = inject(NavigationService);
  private translate = inject(TranslateService);
  protected navigationItems: NavigationItem[] = [];
  protected readonly environment = environment;

  /** Raw items before translation */
  private navKeyItems: NavigationItem[] = [];

  private async initializeNavigation(): Promise<void> {
    const admin = await this.roleService.checkAccess(Role.Administrator);
    const isOperatorAdmin = await this.roleService.checkAccess(Role.OperatorAdmin);
    const isOperatorMember = await this.roleService.checkAccess(Role.OperatorMember);
    const hasCameraExport = await this.roleService.checkAccess(Role.CameraDataExport);
    const isAuthenticated = this.roleService.isAuthenticated();

    const items: NavigationItem[] = [];

    // 1. Overview / Dashboard
    if (isAuthenticated) {
      items.push({
        id: 'home',
        label: 'nav.overview',
        icon: 'dashboard',
        route: '/'
      });
    }

    // 2. Operations Group (Accessible by OperatorAdmin, OperatorMember, and Administrator)
    if (isOperatorAdmin || isOperatorMember || admin) {
      const operationChildren: NavigationItem[] = [];

      // Parking Spaces (OperatorAdmin & OperatorMember)
      operationChildren.push({
        id: 'parking-space',
        label: 'nav.parkingSpace',
        icon: 'local_parking',
        route: '/parking-space'
      });

      // Parking Rights (OperatorAdmin & Administrator)
      if (isOperatorAdmin || admin) {
        operationChildren.push({
          id: 'parking-rights',
          label: 'nav.parkingRights',
          icon: 'verified_user',
          route: '/parking-rights'
        });

        operationChildren.push({
          id: 'license-plates',
          label: 'nav.licensePlates',
          icon: 'directions_car',
          route: '/license-plates'
        });

        operationChildren.push({
          id: 'display-management',
          label: 'nav.displayManagement',
          icon: 'tv',
          route: '/displays'
        });

        // Organizations sits with Operations rather than Administration: the list is scoped
        // server-side to the organizations holding a pool parking config on a space this operator
        // was granted, so it is day-to-day operator work, not an estate-wide admin view. The two
        // Administrator-only actions inside it (delete, log in as org) keep their own guards.
        operationChildren.push({
          id: 'org-management',
          label: 'nav.organizations',
          icon: 'corporate_fare',
          route: '/org-management'
        });
      }

      items.push({
        id: 'operations-group',
        label: 'nav.operations',
        icon: 'tune',
        sectionHeader: 'nav.sections.operations',
        expanded: true,
        children: operationChildren
      });
    }

    // 3. Finance & Billing Group
    // Payments are open to operator admins: every payment/obligation endpoint scopes to the caller's
    // own spaces from the token (only an Administrator may widen it), and the site selector lists only
    // the operator's spaces. Tariffs stay Administrator-only — they decide what every parker is charged.
    if (isOperatorAdmin || admin) {
      const financeChildren: NavigationItem[] = [];

      // Payment Obligations & Sweeps
      financeChildren.push({
        id: 'payment-obligations',
        label: 'nav.paymentObligations',
        icon: 'receipt_long',
        route: '/payment-obligations'
      });

      if (admin) {
        financeChildren.push({
          id: 'tariff-management',
          label: 'nav.tariffManagement',
          icon: 'sell',
          route: '/tariff-management'
        });
      }

      items.push({
        id: 'finance-group',
        label: 'nav.finance',
        icon: 'payments',
        sectionHeader: 'nav.sections.finance',
        children: financeChildren
      });
    }

    // 4. Administration Group (Administrator only)
    if (admin) {
      const adminChildren: NavigationItem[] = [];

      adminChildren.push({
        id: 'customer-management',
        label: 'nav.customerManagement',
        icon: 'business',
        route: '/customer-management'
      });

      adminChildren.push({
        id: 'user-management',
        label: 'nav.userManagement',
        icon: 'manage_accounts',
        route: '/user-management',
        children: [
          {
            id: 'operator-management',
            label: 'nav.operatorManagement',
            icon: 'admin_panel_settings',
            route: '/user-management/operator-management'
          },
          {
            id: 'group-management',
            label: 'nav.groupManagement',
            icon: 'group_work',
            route: '/user-management'
          }
        ]
      });

      items.push({
        id: 'admin-group',
        label: 'nav.administration',
        icon: 'admin_panel_settings',
        sectionHeader: 'nav.sections.administration',
        expanded: false,
        children: adminChildren
      });
    }

    // 5. System Tools & Diagnostics Group
    if (admin || hasCameraExport) {
      const toolChildren: NavigationItem[] = [];

      if (hasCameraExport || isOperatorAdmin || admin) {
        toolChildren.push({
          id: 'camera-export',
          label: 'nav.detectionExport',
          icon: 'download',
          route: '/camera-export'
        });
      }

      if (admin) {
        toolChildren.push({
          id: 'migration-validation',
          label: 'nav.migrationValidation',
          icon: 'verified',
          route: '/migration-validation'
        });

        toolChildren.push({
          id: 'testing-push',
          label: 'nav.testingPush',
          icon: 'send',
          route: '/testing-push'
        });
      }

      if (toolChildren.length > 0) {
        items.push({
          id: 'system-tools-group',
          label: 'nav.systemTools',
          icon: 'build',
          sectionHeader: 'nav.sections.system',
          expanded: false,
          children: toolChildren
        });
      }
    }

    // 6. User Account & Session
    if (isAuthenticated) {
      const accountChildren: NavigationItem[] = [];

      accountChildren.push({
        id: 'my-account',
        label: 'nav.myAccount',
        icon: 'account_circle',
        route: '/my-account'
      });

      accountChildren.push({
        id: 'logout',
        label: 'nav.logout',
        icon: 'logout',
        action: () => {
          this.keycloak.logout();
        }
      });

      items.push({
        id: 'account-group',
        label: 'nav.sections.account',
        icon: 'person',
        sectionHeader: 'nav.sections.account',
        expanded: true,
        children: accountChildren
      });
    }

    this.navKeyItems = items;
    this.applyTranslations();
    this.translate.onLangChange.subscribe(() => this.applyTranslations());
  }

  private applyTranslations(): void {
    this.navigationItems = this.translateItems(this.navKeyItems);
    this.navigationService.setNavigationItems(this.navigationItems);
  }

  private translateItems(items: NavigationItem[]): NavigationItem[] {
    return items.map(item => ({
      ...item,
      label: this.translate.instant(item.label),
      sectionHeader: item.sectionHeader ? this.translate.instant(item.sectionHeader) : undefined,
      children: item.children ? this.translateItems(item.children) : item.children
    }));
  }

  ngOnInit(): void {
    this.initializeNavigation();
  }
}
